import type { Href } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { cities } from '@/lib/events-data';
import { currencySymbol } from '@todo/core/i18n/format';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { tourIsPrivate, type Tour } from '@todo/core/tours';
import { WINE_COMPARE_LIMIT } from '@/lib/wine-tours';

export type WineTourTableProps = {
  tours: Tour[];
  /** Hub only — on a city page every tour departs from the same town. */
  showCity: boolean;
};

export type WineTourRow = {
  id: string;
  href: Href;
  title: string;
  city: string;
  duration: string;
  format: string;
  rating: string;
  price: string;
};

export type WineTourColumns = {
  tour: string;
  from: string;
  duration: string;
  format: string;
  rating: string;
  price: string;
};

const EMPTY = '—';

/**
 * The comparison table's cells, shared by the web <table> and the native rows
 * so both show the same facts. Everything comes from the seeded card fields
 * (plus the baked PRIVATE_TOUR flag), so the build render and hydration agree.
 */
export function useWineTourRows(tours: Tour[]): { rows: WineTourRow[]; columns: WineTourColumns } {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const rows = tours.slice(0, WINE_COMPARE_LIMIT).map((tour) => {
    const symbol = currencySymbol(tour.currency);
    return {
      id: tour.id,
      href: { pathname: '/[lang]/tours/[id]', params: { lang: locale, id: tour.id } } as Href,
      title: localizedTourTitle(tour, locale),
      city: cities.find((c) => c.id === tour.city_id)?.name ?? EMPTY,
      duration: tour.duration ?? EMPTY,
      format: t(tourIsPrivate(tour) ? 'wineTours.compare.private' : 'wineTours.compare.group'),
      rating:
        tour.rating != null && tour.review_count
          ? `${tour.rating.toFixed(1)} (${tour.review_count})`
          : EMPTY,
      price: tour.from_price != null ? `${symbol}${Math.round(tour.from_price)}` : EMPTY,
    };
  });

  return {
    rows,
    columns: {
      tour: t('wineTours.compare.tour'),
      from: t('wineTours.compare.from'),
      duration: t('wineTours.compare.duration'),
      format: t('wineTours.compare.format'),
      rating: t('wineTours.compare.rating'),
      price: t('wineTours.compare.price'),
    },
  };
}
